import React from 'react'

import { cn } from '@/lib/utils'
import Skeleton from '@/components/livecontact/Skeleton'

type Props = {
  classInner?: string
}

/** Contact grid cell placeholder: icon tile, label line, value line. */
const CellSkeleton = () => (
  <div className="flex min-h-[44px] items-center gap-2.5">
    <Skeleton className="size-8 rounded-[8px]" />
    <div className="flex flex-1 flex-col gap-1.5">
      <Skeleton className="h-2.5 w-[42%] rounded-[4px]" />
      <Skeleton className="h-3 w-[76%] rounded-[4px]" />
    </div>
  </div>
)

/**
 * Loading state that mirrors the real card exactly — hero band, logo plate, identity, meta,
 * description, social strip, keyword chips, action rows and the contact grid — so nothing
 * shifts when the data lands.
 */
export const LinkPreviewSkeleton = ({ classInner }: Props) => (
  <div
    role="status"
    aria-label="Loading contact card"
    className={cn(
      'w-full overflow-hidden rounded-[28px] border border-line bg-surface shadow-surface',
      classInner
    )}
  >
    {/* Hero band */}
    <Skeleton className="h-[140px] w-full rounded-none" />

    {/* Logo plate */}
    <div className="relative z-10 -mt-12 px-6">
      <div className="size-24 rounded-full bg-surface p-1">
        <Skeleton className="size-full rounded-full" />
      </div>
    </div>

    <div className="flex flex-col gap-3 px-6 pb-5 pt-3">
      {/* Identity */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-[52%] rounded-[6px]" />
        <Skeleton className="h-3.5 w-[70%] rounded-[4px]" />
      </div>

      {/* Meta line */}
      <div className="border-b border-line pb-[13px]">
        <Skeleton className="h-3 w-[34%] rounded-[4px]" />
      </div>

      {/* Description */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3.5 w-full rounded-[4px]" />
        <Skeleton className="h-3.5 w-[92%] rounded-[4px]" />
        <Skeleton className="h-3.5 w-[64%] rounded-[4px]" />
      </div>

      {/* Social strip */}
      <div className="flex gap-3 border-b border-line pb-[13px]">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="size-4 rounded-[4px]" />
        ))}
      </div>

      {/* Keyword chips */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        <Skeleton className="h-6 w-[120px] rounded-full" />
        <Skeleton className="h-6 w-[92px] rounded-full" />
        <Skeleton className="h-6 w-[104px] rounded-full" />
      </div>
    </div>

    {/* Actions */}
    <div className="flex flex-col gap-2 px-6 pb-5 pt-3">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-12 w-full rounded-[12px]" />
      ))}
    </div>

    {/* Contact grid */}
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-6 pb-6 pt-3 sm:grid-cols-1">
      <CellSkeleton />
      <CellSkeleton />
    </div>
  </div>
)

export default LinkPreviewSkeleton
